import React, { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Page } from '../../layout/Page';
import { useOrgName, useShellStatus, useTerms } from '../../layout';
import { pluralTerm, termError } from '../../design/terms';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { Alert, Button, Card, Dialog, EmptyState, ErrorState, Field, Input, useToast } from '../../components/shared';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { childrenByParent, defaultHierarchyNode, isAbbreviatedLevelName, lower, pluralOf, subtreeIds } from './adminModel';
import { useAdminData } from './useAdminData';
import { useOrgFullName } from './adminShared';
import './admin.css';

const SUBTITLE = (org, terms) => `Group ${terms.communitiesLower} into as many levels as ${org} needs. Level names appear in filters, reports, and exports.`;

function emptyEdits() {
  return { levels: null, names: {}, parents: {}, communityNodes: {}, terms: null };
}

export function AdminHierarchyPage() {
  const org = useOrgName();
  const orgFull = useOrgFullName();
  const terms = useTerms();
  const { refresh: refreshShell } = useShellStatus();
  const toast = useToast();
  const [params] = useSearchParams();
  const { data, loading, error, retry, reload } = useAdminData(
    { levels: adminAPI.getLevels, nodes: adminAPI.getNodes, communities: adminAPI.getCommunities, mappings: adminAPI.getMappings, users: adminAPI.getUsers },
    { optional: ['users'] }
  );
  const [edits, setEdits] = useState(emptyEdits);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [addLevelOpen, setAddLevelOpen] = useState(false);
  const nameInput = useRef(null);

  const model = useMemo(() => {
    if (!data) return null;
    const levels = edits.levels || data.levels.map((l) => ({ id: l.id, displayName: l.displayName, pluralDisplayName: l.pluralDisplayName }));
    const levelById = new Map(data.levels.map((l) => [l.id, l]));
    const editedLevel = new Map(levels.filter((l) => l.id).map((l) => [l.id, l]));
    const nodes = data.nodes
      .filter((n) => n.isActive !== false)
      .map((n) => ({ ...n, name: edits.names[n.id] ?? n.name, parentId: n.id in edits.parents ? edits.parents[n.id] : n.parentId }));
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const communities = data.communities
      .filter((c) => c.isActive !== false)
      .map((c) => ({ ...c, hierarchyNodeId: c.id in edits.communityNodes ? edits.communityNodes[c.id] : c.hierarchyNodeId }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const levelOf = (node) => {
      const level = levelById.get(node.levelId);
      return level ? { ...level, ...(editedLevel.get(level.id) || {}) } : null;
    };
    const leafLevelId = data.levels.length ? data.levels[data.levels.length - 1].id : null;
    return { levels, nodes, nodeById, communities, levelOf, leafLevelId, kids: childrenByParent(nodes) };
  }, [data, edits]);

  const termDraft = edits.terms || { community: terms.community, communities: terms.communities };
  const termChanged = Boolean(edits.terms) && (termDraft.community.trim() !== terms.community || termDraft.communities.trim() !== terms.communities);
  const termProblem = termError(termDraft.community, 'singular') || termError(termDraft.communities, 'plural');
  const dirty = Boolean(edits.levels) || Object.keys(edits.names).length > 0 || Object.keys(edits.parents).length > 0 || Object.keys(edits.communityNodes).length > 0 || termChanged;

  const requested = params.get('node');
  const selectedId = useMemo(() => {
    if (!model) return null;
    if (selected && model.nodeById.has(selected)) return selected;
    if (requested) {
      const match = model.nodes.find((n) => n.id === requested || lower(n.name) === lower(requested));
      if (match) return match.id;
    }
    return defaultHierarchyNode(model.nodes, model.leafLevelId, model.communities, data?.mappings);
  }, [model, selected, requested, data]);

  const discard = () => {
    setEdits(emptyEdits());
    setSaveError(null);
  };

  const save = async () => {
    if (!model) return;
    if (!dirty) {
      toast('No changes to save.', 'info');
      return;
    }
    if (termChanged && termProblem) {
      toast(termProblem, 'alert');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (edits.levels) {
        await adminAPI.saveLevels(edits.levels.map((l) => ({ id: l.id, displayName: l.displayName.trim(), pluralDisplayName: l.pluralDisplayName })));
      }
      for (const [id, name] of Object.entries(edits.names)) {
        const node = data.nodes.find((n) => n.id === id);
        if (node && node.name !== name) await adminAPI.updateNode({ ...node, name });
      }
      for (const [id, parentId] of Object.entries(edits.parents)) await adminAPI.moveNode(id, parentId);
      for (const [id, nodeId] of Object.entries(edits.communityNodes)) {
        const community = data.communities.find((c) => c.id === id);
        if (community) await adminAPI.updateCommunity({ ...community, hierarchyNodeId: nodeId });
      }
      let communityName = terms.community;
      if (termChanged) {
        const saved = await adminAPI.saveTerminology({ communitySingular: termDraft.community.trim(), communityPlural: termDraft.communities.trim() });
        communityName = saved?.communitySingular || termDraft.community.trim();
        await refreshShell?.();
      }
      const names = model.levels.map((l) => l.displayName.trim()).filter(Boolean);
      setEdits(emptyEdits());
      await reload();
      toast(`Saved hierarchy. Filters now read “${[...names, communityName].join(' › ')}”`);
    } catch (err) {
      setSaveError(err);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <>
      <Button onClick={discard} disabled={saving}>
        Discard changes
      </Button>
      <Button variant="primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save hierarchy'}
      </Button>
    </>
  );

  if (error) {
    return (
      <Page title="Hierarchy" subtitle={SUBTITLE(org, terms)}>
        <ErrorState title="Hierarchy couldn't load" body="Vertolytics couldn't read the hierarchy. Nothing was changed." error={error} onRetry={retry} />
      </Page>
    );
  }

  if (loading || !model) {
    return (
      <Page title="Hierarchy" subtitle={SUBTITLE(org, terms)} actions={actions}>
        <div className="grid">
          <div className="c-5 stack">
            <Card title="Levels" description="From broadest to the level calls are attributed to" loading>
              <div style={{ height: 160 }} />
            </Card>
            <Card title="Preview" description="How the filter bar will read on every dashboard" loading>
              <div style={{ height: 60 }} />
            </Card>
          </div>
          <Card className="c-7" title={orgFull} loading>
            <div style={{ height: 380 }} />
          </Card>
        </div>
      </Page>
    );
  }

  const addLevelDialog = (
    <AddLevelDialog
      open={addLevelOpen}
      onClose={() => setAddLevelOpen(false)}
      topLevel={data.levels[0] || null}
      topCount={model.nodes.filter((n) => !n.parentId).length}
      onAdded={async (name) => {
        setAddLevelOpen(false);
        setEdits(emptyEdits());
        await reload();
        toast(`Added the ${name} level. Name its groups and move groups under them.`);
      }}
    />
  );

  if (data.levels.length === 0) {
    return (
      <Page title="Hierarchy" subtitle={SUBTITLE(org, terms)} actions={actions}>
        <EmptyState
          icon="sitemap"
          title="No groups yet"
          body={`Every ${terms.communityLower} currently sits directly under ${orgFull}. Add a level, such as Division, to group ${terms.communitiesLower} for filtering and reports.`}
          actions={
            <Button variant="primary" onClick={() => setAddLevelOpen(true)}>
              Add a level
            </Button>
          }
        />
        {addLevelDialog}
      </Page>
    );
  }

  const setTermSingular = (value) =>
    setEdits((e) => {
      const current = e.terms || { community: terms.community, communities: terms.communities };
      const pluralFollows = !current.communities.trim() || current.communities.trim() === pluralTerm(current.community.trim());
      return { ...e, terms: { community: value, communities: pluralFollows ? pluralTerm(value.trim()) : current.communities } };
    });
  const setLevelName = (index, value) =>
    setEdits((e) => {
      const levels = (e.levels || model.levels).map((l, i) => {
        if (i !== index) return l;
        const original = data.levels.find((o) => o.id === l.id);
        return { ...l, displayName: value, pluralDisplayName: original && original.displayName === value ? original.pluralDisplayName : '' };
      });
      return { ...e, levels };
    });
  const removeLevel = (index) => {
    if (model.levels.length <= 1) {
      toast(`Keep at least one level above ${terms.communitiesLower}.`, 'alert');
      return;
    }
    setEdits((e) => ({ ...e, levels: (e.levels || model.levels).filter((_, i) => i !== index) }));
  };

  const abbreviated = model.levels.some((l) => isAbbreviatedLevelName(l.displayName));
  const groupLevel = model.levels[model.levels.length - 1];
  const groupLevelName = groupLevel?.displayName || 'Group';
  const topLevelNodes = model.kids.get('') || [];
  const unassigned = model.communities.filter((c) => !c.hierarchyNodeId || !model.nodeById.has(c.hierarchyNodeId));

  const addGroup = async () => {
    const level = data.levels[data.levels.length - 1];
    const parentLevel = data.levels.length > 1 ? data.levels[data.levels.length - 2] : null;
    const parent = parentLevel ? model.nodes.find((n) => n.levelId === parentLevel.id) : null;
    const existing = new Set(model.nodes.map((n) => lower(n.name)));
    let name = `New ${lower(level.displayName)}`;
    for (let i = 2; existing.has(lower(name)); i += 1) name = `New ${lower(level.displayName)} ${i}`;
    try {
      const created = await adminAPI.createNode({ levelId: level.id, parentId: parent ? parent.id : null, name, aliases: [], isActive: true, sortOrder: model.nodes.length });
      await reload();
      setSelected(created.id);
      toast(`Added “${name}”. Name it and drag ${terms.communitiesLower} into it.`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    }
  };

  const deleteNode = async (node) => {
    try {
      await adminAPI.deleteNode(node.id);
      setSelected(null);
      await reload();
      toast(`Deleted ${node.name}`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    }
  };

  const dropOn = (target, payload) => {
    const level = model.levelOf(target);
    if (payload.kind === 'community') {
      if (target.levelId !== model.leafLevelId) return;
      const original = data.communities.find((c) => c.id === payload.id);
      setEdits((e) => {
        const communityNodes = { ...e.communityNodes, [payload.id]: target.id };
        if (original && original.hierarchyNodeId === target.id) delete communityNodes[payload.id];
        return { ...e, communityNodes };
      });
      setSelected(target.id);
    } else if (payload.kind === 'node') {
      const node = model.nodeById.get(payload.id);
      if (!node || node.id === target.id || subtreeIds(node.id, model.nodes).has(target.id)) return;
      const nodeLevel = model.levelOf(node);
      if (!nodeLevel || !level || nodeLevel.order !== level.order + 1) return;
      setEdits((e) => ({ ...e, parents: { ...e.parents, [node.id]: target.id } }));
    }
  };

  return (
    <Page title="Hierarchy" subtitle={SUBTITLE(org, terms)} actions={actions}>
      {saveError && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="error" title="Hierarchy couldn't be saved">
            {errorMessage(saveError)} Your other edits are kept on this page until you leave.
          </Alert>
        </div>
      )}
      <div className="grid">
        <div className="c-5 stack">
          <section className="card">
            <div className="card-head">
              <div>
                <div className="t">Levels</div>
                <div className="d">From broadest to the level calls are attributed to</div>
              </div>
            </div>
            <div className="card-body">
              <div>
                {model.levels.map((level, i) => (
                  <div className="lvl" key={level.id || `new-${i}`}>
                    <span className="muted">
                      <Icon name="grip" />
                    </span>
                    <span className="lv-badge">Level {i + 1}</span>
                    <input className="input" value={level.displayName} aria-label={`Name for level ${i + 1}`} onChange={(e) => setLevelName(i, e.target.value)} />
                    <span>
                      <button type="button" className="btn btn-ghost btn-sm icon-btn" aria-label="Remove level" title="Remove level" onClick={() => removeLevel(i)}>
                        <Icon name="trash" className="ico-sm" />
                      </button>
                    </span>
                  </div>
                ))}
                <div className="lvl">
                  <span className="muted">
                    <Icon name="grip" />
                  </span>
                  <span className="lv-badge">Level {model.levels.length + 1}</span>
                  <input
                    className="input"
                    value={termDraft.community}
                    aria-label={`Name for level ${model.levels.length + 1}`}
                    title={`Your organization's word for ${terms.aCommunity}. Calls always attach at this level.`}
                    onChange={(e) => setTermSingular(e.target.value)}
                  />
                  <span>
                    <span className="caption">Calls attach here</span>
                  </span>
                </div>
                {(edits.terms || !terms.isDefault) && (
                  <div className="lvl">
                    <span />
                    <span className="lv-badge">Plural</span>
                    <input
                      className="input"
                      value={termDraft.communities}
                      aria-label={`Plural of ${termDraft.community || 'the last level'}`}
                      onChange={(e) => setEdits((ed) => ({ ...ed, terms: { ...termDraft, communities: e.target.value } }))}
                    />
                    <span>
                      <span className="caption">Lists and reports</span>
                    </span>
                  </div>
                )}
                {edits.terms && termProblem && (
                  <div className="field-error mt-2">
                    <Icon name="alert" className="ico-sm" />
                    {termProblem}
                  </div>
                )}
                {abbreviated && (
                  <div className="field-error mt-2">
                    <Icon name="alert" className="ico-sm" />
                    Use full names; abbreviations aren't allowed in level names.
                  </div>
                )}
              </div>
              <button className="btn btn-sm mt-3" type="button" onClick={() => setAddLevelOpen(true)} disabled={dirty} title={dirty ? 'Save or discard your changes first' : undefined}>
                Add a level above
              </button>
            </div>
          </section>
          <section className="card">
            <div className="card-head">
              <div>
                <div className="t">Preview</div>
                <div className="d">How the filter bar will read on every dashboard</div>
              </div>
            </div>
            <div className="card-body">
              <div className="row-wrap">
                {[...model.levels.map((l) => l.displayName), termDraft.community].map((name, i, all) => (
                  <React.Fragment key={`${name}-${i}`}>
                    {i > 0 && <Icon name="chevR" className="ico-sm muted" />}
                    <span className="chip">
                      <Icon name={i === all.length - 1 ? 'home' : 'sitemap'} />
                      <span className="k">{name || 'Unnamed'}</span>All
                      <Icon name="chevD" className="ico-sm" />
                    </span>
                  </React.Fragment>
                ))}
              </div>
              <p className="caption mt-3">Reports and exports use the same names as column headers.</p>
            </div>
          </section>
        </div>
        <section className="card c-7">
          <div className="card-head">
            <div>
              <div className="t">{orgFull}</div>
              <div className="d">
                {`${model.nodes.filter((n) => n.levelId === model.leafLevelId).length} ${lower(pluralOf(groupLevel))}, ${model.communities.length} ${terms.communitiesLower}. Drag to move ${terms.aCommunity} between ${lower(pluralOf(groupLevel))}.`}
              </div>
            </div>
            <div className="acts">
              <button className="btn btn-sm" type="button" onClick={addGroup}>
                Add {lower(groupLevelName)}
              </button>
            </div>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,.9fr)', gap: 16 }}>
            <div className="tree2" role="tree" aria-label="Hierarchy">
              <div className="node" style={{ fontWeight: 600 }}>
                <Icon name="building" className="ico-sm" />
                {orgFull}
              </div>
              <div className="kids">
                {topLevelNodes.map((node) => (
                  <TreeNode key={node.id} node={node} model={model} selectedId={selectedId} onSelect={setSelected} onDrop={dropOn} />
                ))}
                {unassigned.map((c) => (
                  <CommunityRow key={c.id} community={c} />
                ))}
              </div>
            </div>
            <div className="panel" style={{ alignSelf: 'start' }}>
              {selectedId ? (
                <NodeDetail
                  key={selectedId}
                  node={model.nodeById.get(selectedId)}
                  model={model}
                  org={orgFull}
                  data={data}
                  terms={terms}
                  inputRef={nameInput}
                  onRename={(name) => setEdits((e) => ({ ...e, names: { ...e.names, [selectedId]: name } }))}
                  onParent={(parentId) => setEdits((e) => ({ ...e, parents: { ...e.parents, [selectedId]: parentId } }))}
                  onDelete={deleteNode}
                />
              ) : (
                <p className="caption">Select a group to see its details.</p>
              )}
            </div>
          </div>
        </section>
      </div>
      {addLevelDialog}
    </Page>
  );
}

const DRAG_TYPE = 'application/x-vertolytics-hierarchy';

function dragProps(payload) {
  return {
    draggable: true,
    onDragStart: (e) => {
      e.stopPropagation();
      e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
      e.dataTransfer.effectAllowed = 'move';
    },
  };
}

function TreeNode({ node, model, selectedId, onSelect, onDrop }) {
  const [over, setOver] = useState(false);
  const level = model.levelOf(node);
  const isLeafLevel = node.levelId === model.leafLevelId;
  const children = model.kids.get(node.id) || [];
  const communities = isLeafLevel ? model.communities.filter((c) => c.hierarchyNodeId === node.id) : [];
  const expanded = selectedId === node.id || (!isLeafLevel && subtreeIds(node.id, model.nodes).has(selectedId));
  const count = isLeafLevel ? communities.length : children.length;
  const selected = selectedId === node.id;

  return (
    <>
      <div
        className={['node', selected ? 'sel' : '', over ? 'drop' : ''].filter(Boolean).join(' ')}
        role="treeitem"
        aria-expanded={expanded}
        aria-selected={selected}
        tabIndex={0}
        onClick={() => onSelect(node.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(node.id);
          }
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes(DRAG_TYPE)) {
            e.preventDefault();
            setOver(true);
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          try {
            onDrop(node, JSON.parse(e.dataTransfer.getData(DRAG_TYPE)));
          } catch (err) {
            // Not a hierarchy drag.
          }
        }}
        {...dragProps({ kind: 'node', id: node.id })}
      >
        <span className="grip">
          <Icon name="grip" className="ico-sm" />
        </span>
        <Icon name={expanded ? 'chevD' : 'chevR'} className="ico-sm" />
        {node.name} <span className="lv-badge">{level?.displayName || ''}</span>
        <span className="cnt">{count}</span>
      </div>
      {expanded && (
        <div className="kids">
          {isLeafLevel
            ? communities.map((c) => <CommunityRow key={c.id} community={c} />)
            : children.map((child) => <TreeNode key={child.id} node={child} model={model} selectedId={selectedId} onSelect={onSelect} onDrop={onDrop} />)}
        </div>
      )}
    </>
  );
}

function CommunityRow({ community }) {
  return (
    <div className="node" role="treeitem" aria-selected={false} {...dragProps({ kind: 'community', id: community.id })}>
      <span className="grip">
        <Icon name="grip" className="ico-sm" />
      </span>
      <Icon name="home" className="ico-sm" />
      {community.name}
      <span className="cnt">{community.homeCount != null ? fmt.int(community.homeCount) : ''}</span>
    </div>
  );
}

function NodeDetail({ node, model, org, data, terms, inputRef, onRename, onParent, onDelete }) {
  if (!node) return null;
  const level = model.levelOf(node);
  const levelName = level?.displayName || 'Group';
  const parentLevel = level && level.order > 0 ? data.levels.find((l) => l.order === level.order - 1) : null;
  const parentOptions = parentLevel ? model.nodes.filter((n) => n.levelId === parentLevel.id) : [];
  const subtree = subtreeIds(node.id, model.nodes);
  const communities = model.communities.filter((c) => subtree.has(c.hierarchyNodeId));
  const communityIds = new Set(communities.map((c) => c.id));
  const homes = communities.reduce((sum, c) => sum + (c.homeCount || 0), 0);
  const mappings = data.mappings.filter((m) => subtree.has(m.hierarchyNodeId) || communityIds.has(m.communityId)).length;
  const users = data.users
    ? data.users.filter((u) => u.isActive && ((u.scopedHierarchyNodeIds || []).some((id) => subtree.has(id)) || (u.scopedCommunityIds || []).some((id) => communityIds.has(id)))).length
    : null;
  const hasChildren = (model.kids.get(node.id) || []).length > 0;
  const deletable = communities.length === 0 && !hasChildren;

  const stats = [
    [terms.communities, communities.length],
    ['Homes', fmt.int(homes)],
    ['Queues mapped here', mappings],
    ['Users scoped here', users == null ? '—' : users],
  ];

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="field">
        <label className="label" htmlFor="dn">
          {levelName} name
        </label>
        <input className="input" id="dn" ref={inputRef} value={node.name} onChange={(e) => onRename(e.target.value)} />
      </div>
      <div className="field">
        <label className="label" htmlFor="dp">
          Parent
        </label>
        {parentLevel ? (
          <select className="select" id="dp" value={node.parentId || ''} onChange={(e) => onParent(e.target.value || null)}>
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        ) : (
          <select className="select" id="dp" value="" disabled={false} onChange={() => {}}>
            <option value="">{org}</option>
          </select>
        )}
      </div>
      {stats.map(([label, value]) => (
        <div className="row between small" key={label}>
          <span className="muted">{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
      <div className="row">
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.focus();
              inputRef.current.select();
            }
          }}
        >
          Rename
        </button>
        <button
          className="btn btn-sm btn-ghost"
          type="button"
          title={deletable ? `Delete ${node.name}` : hasChildren ? 'Move the groups under it first' : `Move its ${terms.communitiesLower} first`}
          disabled={!deletable}
          onClick={() => onDelete(node)}
        >
          <Icon name="trash" className="ico-sm" />
          Delete
        </button>
      </div>
      <p className="caption">
        A {lower(levelName)} can be deleted once it has no {terms.communitiesLower}.
      </p>
    </div>
  );
}

function AddLevelDialog({ open, onClose, topLevel, topCount, onAdded }) {
  const [name, setName] = useState('');
  const [parentName, setParentName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const abbreviated = isAbbreviatedLevelName(name);
  const needsParent = Boolean(topLevel) && topCount > 0;

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await adminAPI.insertLevelAbove({ displayName: name.trim(), parents: [], defaultParentName: needsParent ? parentName.trim() : undefined });
      setName('');
      setParentName('');
      await onAdded(name.trim());
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={topLevel ? 'Add a level above' : 'Add a level'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || !name.trim() || abbreviated || (needsParent && !parentName.trim())}>
            {busy ? 'Adding…' : 'Add level'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Level name" htmlFor="nl-name" hint="Full words, singular, for example Region or Division." error={abbreviated ? "Use full names; abbreviations aren't allowed in level names." : null}>
          <Input id="nl-name" value={name} invalid={abbreviated} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        {needsParent && (
          <Field
            label={`Name of the ${lower(name) || 'new level'} that holds the current ${lower(pluralOf(topLevel))}`}
            htmlFor="nl-parent"
            hint={`All ${topCount} ${lower(pluralOf(topLevel))} move under it. You can add more and move ${lower(pluralOf(topLevel))} afterwards.`}
          >
            <Input id="nl-parent" value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="For example North Region" />
          </Field>
        )}
        {problem && <Alert tone="error">{problem}</Alert>}
      </div>
    </Dialog>
  );
}
